/* eslint-disable */
import * as React from 'react';
import { FunctionComponent, useContext, useEffect, useRef } from 'react';

//OpenLayers
import OlOverlay from 'ol/Overlay';
import OlGeomPolygon from 'ol/geom/Polygon';
import OlGeomPoint from 'ol/geom/Point';
import OlGeomCircle from 'ol/geom/Circle';
import OlGeomLineString from 'ol/geom/LineString';
import { unByKey } from 'ol/Observable';

//Custom components
import MapContext from '@/context/MapContext/MapContext';
import { formatArea, formatLength, formatPoint, getDefinedOptions, getEvents } from '@/lib/olHelpers';

//Types
import { MapContextType } from '@/context/MapContext/MapContext';
import { IMeasureTooltipOverlay } from '@/@types/components/Map/Overlays';
import { Coordinate } from 'ol/coordinate';

const MeasureTooltipOverlay: FunctionComponent<IMeasureTooltipOverlay> = (props) => {
  const context = useContext(MapContext) as MapContextType;

  const overlayDiv = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<OlOverlay | null>(null);
  const listenerRef = useRef<any | null>(null);

  if (!overlayDiv.current) {
    const el = document.createElement('div');
    el.className = 'ol-tooltip ol-tooltip-static';
    el.style.display = 'block';
    el.style.font = '12px sans-serif';
    el.style.color = '#000';
    el.style.textAlign = 'center';
    el.style.textShadow = '2px 2px 0 #fff, -2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff';
    el.style.padding = '2px 4px';
    el.style.borderRadius = '2px';
    el.style.backgroundColor = 'rgba(255,255,255,0.2)';
    overlayDiv.current = el;
  }

  const { feature } = props;

  const options = {
    id: undefined,
    element: undefined,
    offset: [0, -15],
    position: undefined,
    positioning: 'bottom-center',
    stopEvent: undefined,
    insertFirst: undefined,
    autoPan: true,
    autoPanAnimation: { duration: 100 },
    autoPanMargin: undefined,
    className: undefined,
  };

  const events = {
    change: undefined,
    'change:element': undefined,
    'change:map': undefined,
    'change:offset': undefined,
    'change:position': undefined,
    'change:positioning': undefined,
    error: undefined,
    propertychange: undefined,
  };

  useEffect(() => {
    let allOptions = Object.assign(options, props);
    //@ts-ignore TODO: Property 'position' does not exist on type 'never'
    allOptions.position = undefined; //we need to set it after adding to map, otherwise autoPan won't work
    let definedOptions = getDefinedOptions(allOptions);

    //@ts-ignore TODO: Property 'element' does not exist on type 'object'
    definedOptions.element = overlayDiv.current;

    const overlay = new OlOverlay(definedOptions);
    overlayRef.current = overlay;

    if (context.map) {
      const mapOverlay = context.map.getOverlayById(props.id);
      if (mapOverlay) {
        context.map.removeOverlay(mapOverlay);
      }
      context.map.addOverlay(overlay);
      if (props.position) {
        //@ts-ignore TODO: Type '"top"' is not assignable to type 'number[] | undefined'
        overlay.setPosition(props.position);
      }
    } else {
      context.initOptions.overlays.push(overlay);
    }

    let olEvents = getEvents(events, props);
    for (let eventName in olEvents) {
      //@ts-ignore TODO:  Argument of type 'string' is not assignable to parameter of type '("error" | "change" | "propertychange")[]'
      overlay.on(eventName, olEvents[eventName]);
    }

    return () => {
      if (context.map && overlayRef.current) {
        context.map.removeOverlay(overlayRef.current);
      }
      overlayRef.current = null;
    };
  }, [context.map, props.id]);

  useEffect(() => {
    if (feature) {
      let geom;
      let output: string;
      let tooltipCoord: Coordinate;

      const updateTooltip = (g: any) => {
        geom = g;

        if (geom instanceof OlGeomPoint) {
          // output = formatPoint(geom);
          // tooltipCoord = geom.getCoordinates();
        } else if (geom instanceof OlGeomLineString) {
          output = formatLength(geom);
          tooltipCoord = geom.getLastCoordinate();
        } else if (geom instanceof OlGeomPolygon) {
          output = formatArea(geom);
          tooltipCoord = geom.getInteriorPoint().getCoordinates();
        } else if (geom instanceof OlGeomCircle) {
          const center = geom.getCenter();
          const radius = geom.getRadius();
          // getRadius() is in map projection units (e.g. Web Mercator), which are
          // stretched vs. true ground distance away from the equator. Measure a
          // center-to-edge line geodesically instead, same as formatLength does.
          const radiusLine = new OlGeomLineString([center, [center[0] + radius, center[1]]]);
          output = formatLength(radiusLine);
          tooltipCoord = center; // center of circle
        }

        if (overlayDiv && overlayDiv.current) {
          //@ts-ignore
          overlayDiv.current.innerHTML = output;
          //@ts-ignore
          overlayDiv.current.style.display = 'block';
        }

        if (context.map) {
          const mapOverlay = context.map.getOverlayById(props.id);
          if (mapOverlay) {
            mapOverlay.setPosition(tooltipCoord);
          }
        }
      };

      // handle immediate geometry
      const g = feature.getGeometry();
      if (g) updateTooltip(g);

      // listen for geometry changes (for live draw)
      const geomChangelistener = g?.on('change', (evt: any) => {
        updateTooltip(evt.target);
      });

      listenerRef.current = geomChangelistener;
    } else {
      if (listenerRef.current) {
        unByKey(listenerRef.current);
        listenerRef.current = null;
      }
      overlayRef.current?.setPosition(undefined);
    }
  }, [feature, context.map, props.id]);

  return null;
};

//@ts-ignore TODO: isTouchDevice does not exist on MeasureTooltipOverlay
MeasureTooltipOverlay.isTouchDevice = function () {
  try {
    document.createEvent('TouchEvent');
    return true;
  } catch (e) {
    return false;
  }
};

export default MeasureTooltipOverlay;
